import React from 'react'
import { Redirect } from 'react-router'
import { Button, Icon } from '@/components'
import { Link } from '@mui/material'
import { AnimatePresence, motion } from 'motion/react'
import Header from '../../Header/HeaderV2'
import './styles.scss'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'
import { logEvent } from '@/logging'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import TextFormField from '@/components/forms/text-form-field'
import useViewModel from './useHeroSectionSignUpViewModel'
import MarketingPixelTracking from '@/components/MarketingPixelTracking/MarketingPixelTracking'

export const HeroSection = () => {
  const viewModel = useViewModel()

  if (viewModel.authenticated) {
    logEvent('signUp')
    return <Redirect to="/app" />
  }

  const validErrors = viewModel.errors ?? {}

  const LoginLink = (
    <p className="text-[#0A1C53] text-[16px] md:text-[20px] mb-0 font-medium">
      Already have an account?{' '}
      <Link style={{ color: '#426FFB', fontWeight: 700 }} href="/login">
        Login
      </Link>
    </p>
  )

  if (viewModel.loadingTheme || viewModel.remoteConfigLoading) {
    return <PageLoader />
  }

  return (
    <div className="signup-root flex flex-col items-center bg-[#EEF2FB] min-h-[100vh]">
      {/* Fire tracking pixel on successful signup */}
      {viewModel.signupSuccess && (
        <MarketingPixelTracking event="SIGNUP" subid={viewModel.referralCode} />
      )}
      <div className="flex flex-col justify-center w-[90%] max-w-5xl min-h-[100vh]">
        {viewModel.whiteLabelData?.brandLogo && (
          <div className="flex justify-center">
            <img
              className="max-w-[70px] max-h-[70px] mt-4 mb-1"
              src={viewModel.whiteLabelData.brandLogo}
            />
          </div>
        )}
        <div
          className="flex flex-wrap md:flex-nowrap justify-between pt-8 pb-6 px-5 md:px-12 md:pt-12 md:pb-8 my-3
          bg-white rounded-[20px] shadow-[0_20px_52px_0_rgba(1,50,107,0.16)]"
        >
          <div className="flex flex-col justify-between basis-full md:basis-[45%] mb-8 md:mb-0">
            <div>
              <Header className="mb-4" />
              <h1 className="text-themed-primary font-bold md:font-extrabold leading-10 md:leading-[3.625rem] mb-0">
                {viewModel.viewConfig?.signupPage?.signupText ??
                  'Create Your Account'}
              </h1>
              {!viewModel.isAva && (
                <p className="text-[#0a1c53] font-medium text-[16px] md:text-[20px] leading-5 md:leading-7 m-1">
                  You're 3 minutes away from joining over 60,000 users saving an
                  average $388 / year!
                </p>
              )}
            </div>
            <img
              src="img/signup-image.webp"
              alt="Charging Vehicle"
              width={311}
              className="self-center hidden md:block"
            />
          </div>
          <div className="flex flex-col justify-between basis-full md:basis-[47%] md:ml-2">
            <AnimatePresence>
              {(validErrors?.detail as string[]) && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden text-themed-error bg-themed-error/20 p-3 pl-4 mb-6 font-semibold rounded-md max-sm:text-sm"
                >
                  {validErrors.detail as string[]}
                </motion.div>
              )}
            </AnimatePresence>
            <form
              action="post"
              noValidate
              onSubmit={viewModel.handleSubmit(viewModel.onSubmit)}
            >
              <div className="flex flex-col gap-6 mb-6">
                <div className="flex flex-col gap-1">
                  <TextFormField
                    label="Full Name"
                    data-testing-id="signup-name"
                    name="name"
                    register={viewModel.register}
                    error={viewModel.getFieldError('name')}
                    showErrorDropdown={true}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <TextFormField
                    type="email"
                    label="Email"
                    data-testing-id="signup-email"
                    name="username"
                    register={viewModel.register}
                    showErrorDropdown={true}
                    error={viewModel.getFieldError('username')}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <TextFormField
                    type="password"
                    label="Password"
                    data-testing-id="signup-password"
                    name="password"
                    register={viewModel.register}
                    error={viewModel.getFieldError('password')}
                    showErrorDropdown={true}
                  />
                </div>
                <TextFormField
                  label="Referral Code (Optional)"
                  data-testing-id="referral-code"
                  name="referral_code"
                  register={viewModel.register}
                  disabled={Boolean(viewModel.paramReferralCode)}
                />
              </div>
              <div className="flex flex-wrap mt-3 md:flex-nowrap">
                <Button
                  className="!bg-themed-primary text-[18px] font-semibold py-3 px-5 rounded-lg w-full font-[montserrat]"
                  id="optiwatt-signup-button"
                  variant="primary"
                  type="submit"
                  disabled={
                    viewModel.loading ||
                    !viewModel.executeRecaptcha ||
                    viewModel.captchaLoading ||
                    !viewModel.isFormValid
                  }
                >
                  Sign Up
                </Button>
              </div>
              {viewModel.showSsoLinks && (
                <div className="flex flex-col gap-4 mt-4">
                  <div className="grid grid-cols-[1fr_50px_1fr]">
                    <div
                      className="border-solid border-b-themed-base-400 h-[13px] -mt-[5px]"
                      style={{ borderBottomWidth: '1px' }}
                    />
                    <div className="flex items-center justify-center font-bold text-xs text-[#424F76]">
                      Or
                    </div>
                    <div
                      className="border-solid border-b-themed-base-400 h-[13px] -mt-[5px]"
                      style={{ borderBottomWidth: '1px' }}
                    />
                  </div>
                  <ContinueWithButton
                    onClick={viewModel.continueWithGoogle}
                    icon={<Icon name="Google" size={22} />}
                  />
                  <ContinueWithButton
                    onClick={viewModel.continueWithApple}
                    icon={<Icon name="Apple" size={22} />}
                  />
                </div>
              )}
            </form>
            <div>
              {!isNativeMobile() && (
                <div className="mt-6 md:mt-9 pl-4 border-l border-solid border-[#c9d1e7]">
                  <p className="text-[12px] md:text-[16px] font-medium text-[#0a1c53] m-0">
                    <Link
                      className="!text-[#426ffb] !font-bold"
                      href="/privacy"
                    >
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link className="!text-[#426ffb] !font-bold" href="/tos">
                      Terms of Service
                    </Link>
                  </p>
                </div>
              )}
              <div className="hidden mt-6 md:block">{LoginLink}</div>
            </div>
          </div>
        </div>
        <div className="mb-4 self-start ml-[6%] md:hidden">{LoginLink}</div>
      </div>
    </div>
  )
}

function ContinueWithButton({
  onClick,
  icon,
}: {
  onClick: () => Promise<void>
  icon: React.ReactNode
}) {
  return (
    <div
      onClick={onClick}
      className="flex flex-row items-center justify-center gap-2 p-3 text-lg font-semibold rounded-lg cursor-pointer hover:bg-themed-grey-200 text-themed-grey-900 bg-themed-grey-100"
    >
      Sign Up with
      {icon}
    </div>
  )
}
